import React from "react";
import {
  Box,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
} from "@mui/material";

const DEFAULT_PAGE_TITLE_MARGIN_BOTTOM = 1.5;

const toSxArray = (sx?: SxProps<Theme>) =>
  Array.isArray(sx) ? sx : sx ? [sx] : [];

export interface PageTitleProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  icon?: React.ReactNode;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  align?: "left" | "center";
  titleVariant?: TypographyProps["variant"];
  titleSx?: SxProps<Theme>;
  subtitleSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  titleRowSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
}

export const PageTitle: React.FC<PageTitleProps> = ({
  title,
  subtitle,
  actions,
  icon,
  startAdornment,
  endAdornment,
  align = "left",
  titleVariant = "h5",
  titleSx,
  subtitleSx,
  contentSx,
  titleRowSx,
  sx,
}) => {
  const isCentered = align === "center";

  return (
    <Box
      sx={[
        {
          mb: DEFAULT_PAGE_TITLE_MARGIN_BOTTOM,
          display: "flex",
          justifyContent: actions
            ? "space-between"
            : isCentered
              ? "center"
              : "flex-start",
          alignItems: "center",
          gap: 2,
          textAlign: align,
        },
        ...toSxArray(sx),
      ]}
    >
      <Box
        sx={[
          {
            display: "flex",
            alignItems: "center",
            justifyContent: isCentered ? "center" : "flex-start",
            gap: 1,
          },
          ...toSxArray(contentSx),
        ]}
      >
        {(icon || startAdornment) && (
          <Box
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              "& .MuiSvgIcon-root": {
                fontSize: titleVariant === "h4" ? 30 : 26,
              },
            }}
          >
            {icon || startAdornment}
          </Box>
        )}
        <Box>
          <Box
            sx={[
              {
                display: "flex",
                alignItems: "center",
                justifyContent: isCentered ? "center" : "flex-start",
                gap: 1,
              },
              ...toSxArray(titleRowSx),
            ]}
          >
            <Typography
              variant={titleVariant}
              component="h1"
              sx={[{ color: "text.primary", fontWeight: 600 }, ...toSxArray(titleSx)]}
            >
              {title}
            </Typography>
            {endAdornment}
          </Box>
          {subtitle && (
            <Typography
              variant="body2"
              sx={[{ color: "text.secondary", mt: 0.5 }, ...toSxArray(subtitleSx)]}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {actions && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default PageTitle;
